import { FC, useState } from 'react';
import styled from 'styled-components';

import { EntityType } from '@/entities/vfo/types';
import { useNotices } from '@/features/notices/useNotices';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { breakpoints, media } from '@/shared/components';
import { getPersonFullName } from '@/shared/helpers/names';
import { Button } from '@/shared/uikitV2/Button';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { RemoveFromCaseModalProps } from '@/widgets/modals/types';

import { ModalContainerV2 } from '../../interwill/containers/ModalContainerV2';

export const RemoveFromCaseModal: FC<RemoveFromCaseModalProps> = (props) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'removeFromCase' });

  const [loading, setLoading] = useState(false);

  const notices = useNotices();
  const { members } = useMembers();
  const { removeMember, removeDocument } = useCases();
  const { closeModal } = useModalsStore();

  const member =
    props.entityType === EntityType.Member
      ? members.find((item) => item.profileId === props.entityId)
      : null;

  const handleRemove = async () => {
    setLoading(true);
    if (props.entityType === EntityType.File) {
      const ok = await removeDocument(props.caseId, String(props.entityId));
      if (!ok) return;
      notices.success(t('successNoticeDoc'));
      setLoading(false);
      closeModal();
      return;
    }
    if (!member?.profileId) return;
    const ok = await removeMember(props.caseId, member?.profileId);
    setLoading(false);
    if (!ok) return;
    notices.success(
      t('successNotice', { fullname: getPersonFullName(member, 'display') || t('invitedMember') }),
    );
    closeModal();
  };

  return (
    <ModalContainerV2 pinBottom title={t('title')}>
      <ListContainer>
        {props.entityType === EntityType.Member ? (
          <>
            <span>{t('text1')}</span>
            <span>{t('text2')}</span>
          </>
        ) : (
          <span>{t('textDoc')}</span>
        )}
      </ListContainer>
      <Buttons>
        <Button type="default" size="middle" onClick={closeModal}>
          {t('cancel')}
        </Button>
        <Button size="middle" onClick={handleRemove} disabled={loading} loading={loading}>
          {t('remove')}
        </Button>
      </Buttons>
    </ModalContainerV2>
  );
};

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  padding: 24px 0;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  gap: 16px;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column-reverse;
  }
`;
